import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  procedureExecutionService,
  type RequestStepAuthorizationDto,
  type RespondStepAuthorizationDto,
  type ResubmitStepAuthorizationDto,
  type StepAuthorizationView,
  type ProcedureExecutionState,
} from '../services/procedure-execution.service';
import { procedureExecutionKeys } from './useProcedureExecution';

export const stepAuthorizationKeys = {
  authorization: (inspectionId: string, stepExecutionId: string) =>
    ['step-authorization', inspectionId, stepExecutionId] as const,
};

export function useStepAuthorization(
  inspectionId: string,
  stepExecutionId: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: stepAuthorizationKeys.authorization(inspectionId, stepExecutionId),
    queryFn: () => procedureExecutionService.getStepAuthorization(inspectionId, stepExecutionId),
    enabled,
    staleTime: 10_000,
  });
}

export function useRequestStepAuthorization(inspectionId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      stepExecutionId,
      dto,
    }: {
      stepExecutionId: string;
      dto: RequestStepAuthorizationDto;
    }) => procedureExecutionService.requestStepAuthorization(inspectionId, stepExecutionId, dto),

    onSuccess: (data, { stepExecutionId }) => {
      qc.setQueryData(stepAuthorizationKeys.authorization(inspectionId, stepExecutionId), data);
      qc.setQueryData<ProcedureExecutionState>(
        procedureExecutionKeys.execution(inspectionId),
        (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            sections: prev.sections.map((sec) => ({
              ...sec,
              steps: sec.steps.map((step) =>
                step.stepExecutionId === stepExecutionId
                  ? { ...step, authorization: { id: data.id, status: data.status } }
                  : step,
              ),
            })),
          };
        },
      );
    },
  });
}

export function useRespondStepAuthorization(inspectionId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      stepExecutionId,
      authorizationId,
      dto,
    }: {
      stepExecutionId: string;
      authorizationId: string;
      dto: RespondStepAuthorizationDto;
    }) =>
      procedureExecutionService.respondStepAuthorization(
        inspectionId,
        stepExecutionId,
        authorizationId,
        dto,
      ),

    onSuccess: (data: StepAuthorizationView, { stepExecutionId }) => {
      qc.setQueryData(stepAuthorizationKeys.authorization(inspectionId, stepExecutionId), data);
      qc.setQueryData<ProcedureExecutionState>(
        procedureExecutionKeys.execution(inspectionId),
        (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            sections: prev.sections.map((sec) => ({
              ...sec,
              steps: sec.steps.map((step) =>
                step.stepExecutionId === stepExecutionId
                  ? { ...step, authorization: { id: data.id, status: data.status } }
                  : step,
              ),
            })),
          };
        },
      );
    },
  });
}

export function useResubmitStepAuthorization(inspectionId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      stepExecutionId,
      authorizationId,
      dto,
    }: {
      stepExecutionId: string;
      authorizationId: string;
      dto: ResubmitStepAuthorizationDto;
    }) =>
      procedureExecutionService.resubmitStepAuthorization(
        inspectionId,
        stepExecutionId,
        authorizationId,
        dto,
      ),

    onSuccess: (data: StepAuthorizationView, { stepExecutionId }) => {
      qc.setQueryData(stepAuthorizationKeys.authorization(inspectionId, stepExecutionId), data);
      qc.setQueryData<ProcedureExecutionState>(
        procedureExecutionKeys.execution(inspectionId),
        (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            sections: prev.sections.map((sec) => ({
              ...sec,
              steps: sec.steps.map((step) =>
                step.stepExecutionId === stepExecutionId
                  ? { ...step, authorization: { id: data.id, status: data.status } }
                  : step,
              ),
            })),
          };
        },
      );
    },
  });
}
